import { useMemo, useState } from 'react';
import { useTemplate } from '../../../../../features/template/TemplateProvider';
import useApi from '../../../../../hook/useApi';
import { ButtonCancel, ButtonPrimary } from '../../../../../utils/Button';
import { GetCurrencySymbol } from '../../../../../utils/Currency';
import { GetFloatNumber } from '../../../../../utils/NumberFormatter';
import { calcVat } from '../../../../../utils/VatCalculator';
import { useInvoiceInfo } from '../InvoiceInfoProvider';

function ModalUpdateItemVat() {
    const { setMessage, closeModal, modalData } = useTemplate();
    const { handleReload } = useInvoiceInfo();
    const { put } = useApi();
    const [loading, setLoading] = useState(false);

    const {
        invoiceId,
        itemId,
        productTitle,
        price = 0,
        quantity = 1,
        vatPercent: initVatPercent = 0,
        vatType: initVatType = 'excluding',
        currency
    } = modalData || {};

    const [formData, setFormData] = useState({
        VatPercent: String(initVatPercent),
        VatType: initVatType
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Live preview
    const preview = useMemo(() => {
        const { netAmount, vatAmount, grossAmount } = calcVat(
            price,
            formData.VatPercent,
            formData.VatType
        );
        return {
            net: netAmount * quantity,
            vat: vatAmount * quantity,
            gross: grossAmount * quantity
        };
    }, [price, quantity, formData.VatPercent, formData.VatType]);

    const sym = GetCurrencySymbol(currency);

    const handleSubmit = (e) => {
        e.preventDefault();
        setLoading(true);
        put(`/api/invoices/${invoiceId}/service-items/${itemId}/update-vat`, {
            VatPercent: parseFloat(formData.VatPercent) || 0,
            VatType: formData.VatType
        })
            .then(({ error, message }) => {
                if (error) throw new Error(message);
                setMessage({ type: 'success', text: message });
                handleReload();
                closeModal();
            })
            .catch((err) => {
                setMessage({ type: 'error', text: err?.message });
            })
            .finally(() => setLoading(false));
    };

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <p className="text-sm text-gray-500 -mt-1 truncate">{productTitle}</p>

            {/* VAT Percent */}
            <div className="flex flex-col gap-1">
                <label htmlFor="VatPercent" className="text-sm font-medium">
                    VAT Percent
                </label>
                <div className="flex items-center gap-2">
                    <input
                        id="VatPercent"
                        type="number"
                        name="VatPercent"
                        value={formData.VatPercent}
                        onChange={handleChange}
                        min={0}
                        max={100}
                        step={0.01}
                        className="w-28 h-8 px-2 text-sm border border-gray-300 rounded focus:outline-none focus:border-primary-500"
                        required
                    />
                    <span className="text-sm text-gray-500">%</span>
                </div>
            </div>

            {/* VAT Type */}
            <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">VAT Type</span>
                <div className="flex flex-col gap-1.5">
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                            type="radio"
                            name="VatType"
                            value="excluding"
                            checked={formData.VatType === 'excluding'}
                            onChange={handleChange}
                            className="radio radio-sm"
                        />
                        <span>Excluding VAT</span>
                        <span className="text-xs text-gray-400">
                            (price is net — VAT added on top)
                        </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm">
                        <input
                            type="radio"
                            name="VatType"
                            value="including"
                            checked={formData.VatType === 'including'}
                            onChange={handleChange}
                            className="radio radio-sm"
                        />
                        <span>Including VAT</span>
                        <span className="text-xs text-gray-400">(price already includes VAT)</span>
                    </label>
                </div>
            </div>

            {/* Live preview */}
            <div className="bg-base-200 rounded-lg px-4 py-3 flex flex-col gap-1.5 text-sm">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-0.5">
                    Preview
                </span>
                <div className="flex justify-between">
                    <span className="text-gray-600">Net Amount</span>
                    <span className="font-medium">
                        {sym}
                        {GetFloatNumber(preview.net)}
                    </span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600">VAT Amount ({formData.VatPercent}%)</span>
                    <span className="font-medium">
                        {sym}
                        {GetFloatNumber(preview.vat)}
                    </span>
                </div>
                <div className="flex justify-between border-t border-base-300 pt-1.5 mt-0.5">
                    <span className="font-semibold">Gross Amount</span>
                    <span className="font-semibold">
                        {sym}
                        {GetFloatNumber(preview.gross)}
                    </span>
                </div>
            </div>

            <div className="flex justify-end gap-2 mt-1">
                <ButtonCancel title="Cancel" onClick={closeModal} />
                <ButtonPrimary title="Save Changes" btnType="submit" loading={loading} />
            </div>
        </form>
    );
}

export default ModalUpdateItemVat;
